import { useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTelegram } from "./useTelegram";
import { getNetworkErrorToastMessage } from "@/contexts/LangContext";
import { isNetworkError } from "@/utils/networkError";
import i18n from "@/i18n";

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export function useFetch() {
  const { initData } = useTelegram();
  const initDataRef = useRef(initData);
  initDataRef.current = initData;
  const client = useMemo(() => {
    const instance = axios.create({
      baseURL: API_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });
    instance.interceptors.request.use((config) => {
      if (!config.headers) {
        config.headers = {};
      }

      const fromState = (initDataRef.current || "").trim();
      const fromWindow =
        typeof window !== "undefined"
          ? String(window.Telegram?.WebApp?.initData || "").trim()
          : "";
      const current = fromState || fromWindow;
      if (current) {
        config.headers["X-Telegram-Init-Data"] = current;
      }

      // Soft RU/BY signal for payment methods when CF country is absent (local/dev / no Telegram).
      const appLang = (i18n.resolvedLanguage || i18n.language || "").trim();
      if (appLang) {
        config.headers["X-App-Language"] = appLang;
      }

      // Mini App session bootstrap must not attach a leftover website JWT.
      if (!config.skipAuthHeader) {
        const accessToken = window.localStorage.getItem("accessToken");
        if (accessToken) {
          config.headers["Authorization"] = `Bearer ${accessToken}`;
        }
      }

      return config;
    });

    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (!originalRequest) {
          return Promise.reject(error);
        }

        if (originalRequest._retry) {
          if (isNetworkError(error) && !originalRequest.skipNetworkErrorToast) {
            toast.error(getNetworkErrorToastMessage(), {
              id: "apily-network-error",
            });
          }
          return Promise.reject(error);
        }

        const status = error.response?.status;
        let errorData = error.response?.data;
        if (typeof errorData === "string") {
          try { errorData = JSON.parse(errorData); } catch { errorData = null; }
        }
        const errCode = errorData?.code;

        if (status === 403 && errCode === "EMAIL_NOT_VERIFIED") {
          return Promise.reject(error);
        }

        if (status === 401 || status === 403) {
          const refreshToken = window.localStorage.getItem("refreshToken");

          if (!refreshToken) {
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          try {
            const refreshResponse = await axios.post(
              `${API_URL}/api/auth/refresh`,
              { refreshToken },
              {
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );

            const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
              refreshResponse.data || {};

            if (newAccessToken) {
              window.localStorage.setItem("accessToken", newAccessToken);
            }
            if (newRefreshToken) {
              window.localStorage.setItem("refreshToken", newRefreshToken);
            }

            originalRequest.headers = originalRequest.headers || {};
            if (newAccessToken) {
              originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
            }

            return instance(originalRequest);
          } catch (refreshError) {
            window.localStorage.removeItem("accessToken");
            window.localStorage.removeItem("refreshToken");
            return Promise.reject(refreshError);
          }
        }

        if (
          isNetworkError(error) &&
          !originalRequest?.skipNetworkErrorToast
        ) {
          toast.error(getNetworkErrorToastMessage(), {
            id: "apily-network-error",
          });
        }

        return Promise.reject(error);
      }
    );
    return instance;
  }, []);

  return client;
}